import React, { useEffect, useMemo, useState } from "react";
import { galleryItems } from "../../data/siteData";
import { Link } from "react-router-dom";

function Gallery() {
  const categories = useMemo(
    () => ["All work", ...new Set(galleryItems.map((item) => item.category))],
    []
  );
  const [category, setCategory] = useState("All work");
  const [selectedIndex, setSelectedIndex] = useState(null);

  const visibleItems = useMemo(
    () =>
      category === "All work"
        ? galleryItems
        : galleryItems.filter((item) => item.category === category),
    [category]
  );

  const selected = selectedIndex === null ? null : visibleItems[selectedIndex];

  useEffect(() => {
    if (selectedIndex === null) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedIndex(null);
      if (event.key === "ArrowRight")
        setSelectedIndex((index) => (index + 1) % visibleItems.length);
      if (event.key === "ArrowLeft")
        setSelectedIndex((index) => (index - 1 + visibleItems.length) % visibleItems.length);
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedIndex, visibleItems.length]);

  return (
    <div className="page page--gallery">
      <section className="page-hero gallery-intro">
        <p className="section-eyebrow">The portfolio</p>
        <h1>Craftsmanship you can see in the details.</h1>
        <p>
          Explore a selection of Touchmark's gates, railings, decorative designs
          and structural work. Choose a category to find what interests you.
        </p>
        <div className="gallery-intro__meta">
          <span>Real project photography</span>
          <span>{galleryItems.length} featured images</span>
        </div>
      </section>

      <section className="section gallery-section" aria-label="Project photo gallery">
        <div className="gallery-toolbar">
          <div>
            <p className="section-eyebrow">Explore our work</p>
            <h2>Designed to last. Made to be seen.</h2>
          </div>
          <span className="gallery-count">
            Showing {visibleItems.length} of {galleryItems.length}
          </span>
        </div>
        <div className="gallery-filters" role="group" aria-label="Filter gallery by category">
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              className={`gallery-filter ${category === item ? "is-active" : ""}`}
              aria-pressed={category === item}
              onClick={() => {
                setCategory(item);
                setSelectedIndex(null);
              }}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="gallery-grid">
          {visibleItems.map((item, index) => (
            <button
              key={`${item.title}-${index}`}
              className="gallery-card gallery-card--interactive"
              type="button"
              onClick={() => setSelectedIndex(index)}
              aria-label={`View larger image: ${item.title}`}
            >
              <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
              <div className="gallery-card__content">
                <p>{item.category}</p>
                <h2>{item.title}</h2>
                <span aria-hidden="true">View photo ↗</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="cta-band">
        <div>
          <p className="section-eyebrow">Have a project in mind?</p>
          <h2>Let's discuss the right metalwork for your space.</h2>
        </div>
        <Link className="button button--solid" to="/contact">
          Request a quote
        </Link>
      </section>

      {selected && (
        <div
          className="gallery-lightbox"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedIndex(null);
          }}
        >
          <div
            className="gallery-lightbox__panel"
            role="dialog"
            aria-modal="true"
            aria-label={`Photo: ${selected.title}`}
          >
            <button
              className="gallery-lightbox__close"
              type="button"
              onClick={() => setSelectedIndex(null)}
              aria-label="Close photo"
              autoFocus
            >
              ×
            </button>
            <img src={selected.image} alt={selected.title} />
            <div className="gallery-lightbox__bottom">
              <div>
                <span>{selected.category}</span>
                <h2>{selected.title}</h2>
              </div>
              <div className="gallery-lightbox__controls">
                <button type="button" onClick={() => setSelectedIndex((index) => (index - 1 + visibleItems.length) % visibleItems.length)} aria-label="Previous image">←</button>
                <span>{selectedIndex + 1} / {visibleItems.length}</span>
                <button type="button" onClick={() => setSelectedIndex((index) => (index + 1) % visibleItems.length)} aria-label="Next image">→</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
